import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Heart, Target, Share2, Mail, BarChart2, TrendingUp, MoreHorizontal, Zap, Globe } from 'lucide-react';

const categories = [
  {
    name: 'Customer Acquisition',
    icon: Users,
    calculators: [
      { name: 'CPC Calculator', path: '/calculators/cost-per-click' },
      { name: 'CPA Calculator', path: '/calculators/cost-per-acquisition' },
      { name: 'CPM Calculator', path: '/calculators/cost-per-mille' },
      { name: 'Cost Per Lead Calculator', path: '/calculators/cost-per-lead' },
      { name: 'CAC Calculator', path: '/calculators/customer-acquisition-cost' },
      { name: 'Cost Per App Install Calculator', path: '/calculators/cost-per-app-install' },
      { name: 'Conversion Rate Calculator', path: '/calculators/conversion-rate' },
    ],
  },
  {
    name: 'Customer Retention and Value',
    icon: Heart,
    calculators: [
      { name: 'Customer Lifetime Value (CLV)', path: '/calculators/customer-lifetime-value' },
      { name: 'Customer Retention Rate', path: '/calculators/customer-retention-rate' },
      { name: 'Average Customer Lifespan', path: '/calculators/average-customer-lifespan' },
      { name: 'Average Order Value', path: '/calculators/average-order-value' },
      { name: 'Average Cart Value', path: '/calculators/average-cart-value' },
      { name: 'Cart Abandonment Rate', path: '/calculators/cart-abandonment-rate' },
      { name: 'Loyalty Program ROI', path: '/calculators/loyalty-program-roi' },
    ],
  },
  {
    name: 'Engagement and Interaction',
    icon: Zap,
    calculators: [
      { name: 'Social Media Engagement Rate', path: '/calculators/social-media-engagement-rate' },
      { name: 'Social Media Interaction Rate', path: '/calculators/social-media-interaction-rate' },
      { name: 'Form Completion Rate', path: '/calculators/form-completion-rate' },
      { name: 'Customer Survey Response Rate', path: '/calculators/customer-survey-response-rate' },
      { name: 'Customer Satisfaction Score', path: '/calculators/customer-satisfaction-score' },
      { name: 'Net Promoter Score (NPS)', path: '/calculators/net-promoter-score' },
      { name: 'Customer Referral Rate', path: '/calculators/customer-referral-rate' },
    ],
  },
  {
    name: 'Email Marketing Performance',
    icon: Mail,
    calculators: [
      { name: 'Email Open Rate', path: '/calculators/email-open-rate' },
      { name: 'Email Click-Through Rate', path: '/calculators/email-click-through-rate' },
      { name: 'Email Unsubscribe Rate', path: '/calculators/email-unsubscribe-rate' },
      { name: 'Email Campaign ROI', path: '/calculators/email-campaign-roi' },
      { name: 'Newsletter Churn Rate', path: '/calculators/newsletter-churn-rate' },
    ],
  },
  {
    name: 'Web and Content Analysis',
    icon: Globe,
    calculators: [
      { name: 'Bounce Rate', path: '/calculators/bounce-rate' },
      { name: 'Blog Post Read Rate', path: '/calculators/blog-post-read-rate' },
      { name: 'Cost Per Website Visit', path: '/calculators/cost-per-website-visit' },
      { name: 'Landing Page Conversion Rate', path: '/calculators/landing-page-conversion-rate' },
      { name: 'Time on Site Value', path: '/calculators/time-on-site-value' },
      { name: 'Content Marketing ROI', path: '/calculators/content-marketing-roi' },
    ],
  },
  {
    name: 'Advertising and Media Performance',
    icon: Target,
    calculators: [
      { name: 'Advertising ROI', path: '/calculators/advertising-roi' },
      { name: 'Return on Ad Spend (ROAS)', path: '/calculators/return-on-ad-spend' },
      { name: 'Share of Voice', path: '/calculators/share-of-voice' },
      { name: 'YouTube View Time Value', path: '/calculators/youtube-view-time-value' },
      { name: 'Cost Per Video View', path: '/calculators/cost-per-video-view' },
      { name: 'Banner Ad Click-Through Rate', path: '/calculators/banner-ad-click-through-rate' },
      { name: 'Effective Cost Per Mille (eCPM)', path: '/calculators/effective-cost-per-mille' },
    ],
  },
  {
    name: 'Social Media and Influencers',
    icon: Share2,
    calculators: [
      { name: 'Social Media ROI', path: '/calculators/social-media-roi' },
      { name: 'Influencer Marketing ROI', path: '/calculators/influencer-marketing-roi' },
      { name: 'Organic Social Media Reach', path: '/calculators/organic-social-media-reach' },
    ],
  },
  {
    name: 'Growth and Market Share',
    icon: TrendingUp,
    calculators: [
      { name: 'Growth Rate', path: '/calculators/growth-rate' },
      { name: 'Market Penetration Rate', path: '/calculators/market-penetration-rate' },
      { name: 'Market Share', path: '/calculators/market-share' },
    ],
  },
];

const CalculatorCategories = () => {
  return (
    <section className="my-12">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Calculator Categories</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((category, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center mb-4">
              <category.icon className="w-8 h-8 text-indigo-500 mr-2" />
              <h3 className="text-lg font-semibold text-gray-800">{category.name}</h3>
            </div>
            <ul className="space-y-2">
              {category.calculators.map((calculator, calcIndex) => (
                <li key={calcIndex}>
                  <Link to={calculator.path} className="text-indigo-600 hover:text-indigo-800 transition-colors">
                    {calculator.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
};

export default CalculatorCategories;
